import { writeFetch, getSession } from '../api';

const BOOKINGS_TABLE = 'service_bookings_1777866100042';
const LISTINGS_TABLE = 'merchant_listings_1777866100042';

// Get auto-approve settings
const getAdminAutoApprove = () => localStorage.getItem('admin_auto_approve_bookings') === 'true';
const getMerchantAutoApprove = () => localStorage.getItem('merchant_auto_approve_bookings') === 'true';

// Status flow logic
const getInitialStatus = (pricingModel) => {
  if (pricingModel === 'free') {
    const adminAutoApprove = getAdminAutoApprove();
    const merchantAutoApprove = getMerchantAutoApprove();
    
    if (adminAutoApprove && merchantAutoApprove) return 'confirmed';
    if (adminAutoApprove && !merchantAutoApprove) return 'pending_merchant';
    return 'pending_admin';
  } else {
    // Paid bookings always need admin approval first
    return 'pending_admin';
  }
};

export const bookingApi = {
  // Create a new booking
  async createBooking(bookingData) {
    const user = getSession();
    if (!user) throw new Error('User must be logged in to create a booking');
    
    const booking = {
      ...bookingData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    // Determine initial status based on auto-approve settings
    const initialStatus = getInitialStatus(booking.pricing_model);
    booking.status = initialStatus;
    
    try {
      // Try to save to database first — NCB assigns the id server-side
      const res = await writeFetch(`create/${BOOKINGS_TABLE}`, {
        method: 'POST',
        body: JSON.stringify(booking)
      });
      
      const data = await res.json();
      if (data.status === 'success') {
        return { success: true, booking: data.data };
      }
    } catch (error) {
      console.warn('Database save failed, falling back to localStorage:', error);
    }
    
    // Fallback to localStorage for mock data
    const fallbackBooking = {
      id: `local_${Date.now()}`,
      ...booking,
    };
    const mockKey = `mock_bookings_${booking.merchant_id}`;
    const existing = JSON.parse(localStorage.getItem(mockKey) || '[]');
    existing.push(fallbackBooking);
    localStorage.setItem(mockKey, JSON.stringify(existing));
    
    return { success: true, booking: fallbackBooking };
  },

  // Get bookings for a merchant
  async getMerchantBookings(merchantId) {
    try {
      const res = await writeFetch(`read/${BOOKINGS_TABLE}?merchant_id=${merchantId}&limit=500&sort=created_at&order=desc`);
      const data = await res.json();
      
      let allBookings = [];
      if (data.status === 'success' && data.data?.length > 0) {
        allBookings = data.data;
      }
      
      // Add mock data from localStorage
      const mockKey = `mock_bookings_${merchantId}`;
      const mockBookings = JSON.parse(localStorage.getItem(mockKey) || '[]');
      allBookings = [...allBookings, ...mockBookings.filter(m => !allBookings.find(a => a.id === m.id))];
      
      return allBookings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } catch (error) {
      console.error('Error fetching merchant bookings:', error);
      return [];
    }
  },

  // Get bookings for a customer
  async getCustomerBookings(customerId) {
    try {
      const res = await writeFetch(`read/${BOOKINGS_TABLE}?customer_id=${customerId}&limit=500&sort=created_at&order=desc`);
      const data = await res.json();
      
      let allBookings = [];
      if (data.status === 'success' && data.data?.length > 0) {
        allBookings = data.data;
      }
      
      // Add mock data from all merchant keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mock_bookings_')) {
          const mockBookings = JSON.parse(localStorage.getItem(key) || '[]');
          const customerBookings = mockBookings.filter(b => b.customer_id === customerId);
          allBookings = [...allBookings, ...customerBookings.filter(m => !allBookings.find(a => a.id === m.id))];
        }
      }
      
      return allBookings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } catch (error) {
      console.error('Error fetching customer bookings:', error);
      return [];
    }
  },

  // Get all bookings (admin)
  async getAllBookings() {
    try {
      const res = await writeFetch(`read/${BOOKINGS_TABLE}?limit=500&sort=created_at&order=desc`);
      const data = await res.json();
      
      let allBookings = [];
      if (data.status === 'success' && data.data?.length > 0) {
        allBookings = data.data;
      }
      
      // Add mock data from all merchant keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mock_bookings_')) {
          const mockBookings = JSON.parse(localStorage.getItem(key) || '[]');
          allBookings = [...allBookings, ...mockBookings.filter(m => !allBookings.find(a => a.id === m.id))];
        }
      }
      
      return allBookings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } catch (error) {
      console.error('Error fetching all bookings:', error);
      return [];
    }
  },

  // Update booking status
  async updateBookingStatus(bookingId, newStatus, merchantId = null) {
    try {
      // Update in database
      const res = await writeFetch(`update/${BOOKINGS_TABLE}/${bookingId}`, {
        method: 'PUT',
        body: JSON.stringify({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
      });
      
      const data = await res.json();
      if (data.status === 'success') {
        return { success: true, booking: data.data };
      }
    } catch (error) {
      console.warn('Database update failed, falling back to localStorage:', error);
    }
    
    // Fallback to localStorage for mock data
    if (merchantId) {
      const mockKey = `mock_bookings_${merchantId}`;
      const mockBookings = JSON.parse(localStorage.getItem(mockKey) || '[]');
      const updated = mockBookings.map(b => b.id === bookingId ? { ...b, status: newStatus, updated_at: new Date().toISOString() } : b);
      localStorage.setItem(mockKey, JSON.stringify(updated));
      
      return { success: true, booking: updated.find(b => b.id === bookingId) };
    }
    
    // Try to find in any merchant's mock data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('mock_bookings_')) {
        const mockBookings = JSON.parse(localStorage.getItem(key) || '[]');
        const booking = mockBookings.find(b => b.id === bookingId);
        if (booking) {
          const updated = mockBookings.map(b => b.id === bookingId ? { ...b, status: newStatus, updated_at: new Date().toISOString() } : b);
          localStorage.setItem(key, JSON.stringify(updated));
          return { success: true, booking: updated.find(b => b.id === bookingId) };
        }
      }
    }
    
    return { success: false, error: 'Booking not found' };
  },

  // Generate available time slots
  generateTimeSlots(duration = 60, startHour = 9, endHour = 17, buffer = 15) {
    const slots = [];
    const slotDuration = parseInt(duration) + parseInt(buffer);
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += slotDuration) {
        if (hour === endHour && minute > 0) break;
        
        const startTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const endMinutes = minute + parseInt(duration);
        const endHourCalc = hour + Math.floor(endMinutes / 60);
        const endMin = endMinutes % 60;
        const endTime = `${endHourCalc.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
        
        slots.push({
          start: startTime,
          end: endTime,
          display: `${startTime} - ${endTime}`,
          available: Math.random() > 0.3 // Simulate some slots being unavailable
        });
      }
    }
    
    return slots;
  },

  // Check if a slot is available
  async isSlotAvailable(merchantId, date, startTime, endTime) {
    try {
      const bookings = await this.getMerchantBookings(merchantId);
      const targetDate = new Date(date).toDateString();
      
      const hasConflict = bookings.some(booking => {
        if (booking.status === 'cancelled' || booking.status === 'rejected' || booking.status === 'refunded') {
          return false;
        }
        
        const bookingDate = new Date(booking.start_time).toDateString();
        if (bookingDate !== targetDate) return false;
        
        const bookingStart = new Date(booking.start_time).getTime();
        const bookingEnd = new Date(booking.end_time || booking.start_time).getTime();
        const slotStart = new Date(`${date}T${startTime}`).getTime();
        const slotEnd = new Date(`${date}T${endTime}`).getTime();
        
        return (slotStart >= bookingStart && slotStart < bookingEnd) ||
               (slotEnd > bookingStart && slotEnd <= bookingEnd) ||
               (slotStart <= bookingStart && slotEnd >= bookingEnd);
      });
      
      return !hasConflict;
    } catch (error) {
      console.error('Error checking slot availability:', error);
      return true; // Default to available if check fails
    }
  },

  // Get booking analytics for merchant
  async getMerchantAnalytics(merchantId) {
    try {
      const bookings = await this.getMerchantBookings(merchantId);
      const now = new Date();
      
      const analytics = {
        total_bookings: bookings.length,
        pending_bookings: bookings.filter(b => b.status === 'pending_merchant' || b.status === 'approved').length,
        confirmed_bookings: bookings.filter(b => b.status === 'confirmed').length,
        completed_bookings: bookings.filter(b => b.status === 'completed').length,
        cancelled_bookings: bookings.filter(b => b.status === 'cancelled' || b.status === 'rejected').length,
        total_revenue: bookings
          .filter(b => (b.status === 'confirmed' || b.status === 'completed') && b.price > 0)
          .reduce((sum, b) => sum + parseFloat(b.price || 0), 0),
        upcoming_bookings: bookings.filter(b => 
          b.status === 'confirmed' && new Date(b.start_time) > now
        ).length,
        conversion_rate: bookings.length > 0 
          ? ((bookings.filter(b => b.status === 'confirmed' || b.status === 'completed').length / bookings.length) * 100).toFixed(1)
          : 0
      };
      
      return analytics;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      return {
        total_bookings: 0,
        pending_bookings: 0,
        confirmed_bookings: 0,
        completed_bookings: 0,
        cancelled_bookings: 0,
        total_revenue: 0,
        upcoming_bookings: 0,
        conversion_rate: 0
      };
    }
  }
};

export default bookingApi;